import { Card, Flex, Input, Skeleton, Typography } from 'antd';
import { useCallback, useState } from 'react';
import PatientReadingsAlert from 'components/organisms/oneplatform/patient-readings';
import { useTranslation } from 'react-i18next';
import BasePagination from 'components/atoms/base-pagination';
import BaseEmpty from 'components/atoms/oneplatform/base-empty';
import { type IPatientDataItem } from 'pages/homeDashboard/hooks/use-patient-readings';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useDebouncedEffect } from 'infrastructure/hooks/utils/use-debounced-effect';
import { KEYBOARD_KEYS } from 'constants/keyboard';
import { PATIENT_READINGS_PAGE_SIZE } from 'pages/homeDashboard/constants';

import s from './styles.module.scss';

const { Text } = Typography;

type PatientReadingsSectionProps = {
  patientReadings?: IPatientDataItem[];
  isLoading?: boolean;
  isFetching?: boolean;
  page?: number;
  total?: number;
  searchKeyword?: string;
  onPageChange?: (page: number) => void;
  onSearchChange?: (keyword: string) => void;
  onSnippetClick?: (patientGuid: string) => void;
};

const PatientReadingsSection = ({
  patientReadings = [],
  isLoading = false,
  isFetching = false,
  page = 1,
  total = 0,
  searchKeyword = '',
  onPageChange,
  onSearchChange,
  onSnippetClick,
}: PatientReadingsSectionProps) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState<string>(searchKeyword);

  useDebouncedEffect({
    callback: () => {
      const trimmedSearchText = searchText.trim();
      if (trimmedSearchText.length >= 2 && onSearchChange) {
        onSearchChange(trimmedSearchText);
      } else if (trimmedSearchText.length === 0 && onSearchChange) {
        onSearchChange('');
      }
    },
    value: searchText,
    delay: 700,
  });

  const showSkeleton = isLoading || isFetching;

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchText('');
    if (onSearchChange) {
      onSearchChange('');
    }
  }, [onSearchChange]);

  const handleSearchChange = useCallback((e: { target: { value: string } }) => {
    const { value } = e.target;
    setSearchText(value);
  }, []);

  return (
    <Card className={s.wrapper}>
      <Flex align="space-between" justify="space-between">
        <Text className={s.title}>
          {t('homeDashboard.patientReadingsSection.patientReadingsTitle')}
        </Text>
        {patientReadings.length > 0 && (
          <BasePagination
            simple
            page={page}
            total={total}
            onChange={handlePageChange}
            pageSize={PATIENT_READINGS_PAGE_SIZE}
          />
        )}
      </Flex>
      <Input
        placeholder={t('homeDashboard.patientReadingsSection.placeholder')}
        className={s.search}
        variant="filled"
        value={searchText}
        onChange={handleSearchChange}
        suffix={
          searchText ? (
            <CloseOutlined
              className={s['search-icon']}
              onClick={handleClearSearch}
              role="button"
              tabIndex={0}
              aria-label={t('homeDashboard.patientReadingsSection.clearButton')}
              onKeyDown={(e) => {
                if (
                  e.key === KEYBOARD_KEYS.ENTER ||
                  e.key === KEYBOARD_KEYS.SPACE
                ) {
                  e.preventDefault();
                  handleClearSearch();
                }
              }}
            />
          ) : (
            <SearchOutlined className={s['search-icon']} />
          )
        }
      />
      <Flex vertical className={s['list-wrapper']}>
        {showSkeleton &&
          Array.from({ length: PATIENT_READINGS_PAGE_SIZE }).map((_, index) => (
            <Skeleton
              key={`alert-skeleton-${index}`}
              active
              avatar
              title={{ width: '40%' }}
              paragraph={{
                rows: 3,
                width: ['60%', '80%', '50%'],
              }}
            />
          ))}
        {!showSkeleton &&
          patientReadings.length > 0 &&
          patientReadings.map((patientData) => (
            <PatientReadingsAlert
              key={patientData.patient.guid}
              data={patientData}
              onSnippetClick={onSnippetClick}
            />
          ))}
        {!showSkeleton && patientReadings.length === 0 && (
          <BaseEmpty
            description={
              searchKeyword === ''
                ? t('homeDashboard.patientReadingsSection.patientReadingsEmpty')
                : t(
                    'homeDashboard.patientReadingsSection.patientReadingsNotFound',
                  )
            }
          />
        )}
      </Flex>
    </Card>
  );
};

export default PatientReadingsSection;
